/**
 * 🆕 ToastContext — notificações leves não-bloqueantes
 *
 * Substitui o anti-padrão `alert()` que estava espalhado em 100+
 * call-sites. Toasts aparecem no canto superior direito, somem
 * automaticamente após N segundos (default 4s), empilham bem.
 *
 * Uso:
 *   const { toast } = useToast();
 *   toast.success('Salvo com sucesso!');
 *   toast.error('Falha ao salvar');
 *   toast.warning('Atenção: dados parciais');
 *   toast.info('Informação opcional');
 *   toast.custom({ title, message, variant, duration });
 *
 * Provider deve ficar no nível do App.
 */

import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { Icon } from '@iconify/react';

const ToastContext = createContext(null);

const TOAST_DEFAULTS = {
  duration: 4000, // ms; 0 = não some sozinho
};

const VARIANT_STYLES = {
  success: { icon: 'mdi:check-circle', color: '#059669', background: '#d1fae5', borderColor: '#10b981' },
  error:   { icon: 'mdi:alert-octagon', color: '#dc2626', background: '#fee2e2', borderColor: '#ef4444' },
  warning: { icon: 'mdi:alert',         color: '#d97706', background: '#fef3c7', borderColor: '#f59e0b' },
  info:    { icon: 'mdi:information',   color: '#2563eb', background: '#dbeafe', borderColor: '#3b82f6' },
};

let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((options) => {
    const id = ++nextId;
    const toast = {
      id,
      variant: 'info',
      duration: TOAST_DEFAULTS.duration,
      ...options,
    };
    setToasts((prev) => [...prev, toast]);
    if (toast.duration > 0) {
      setTimeout(() => dismiss(id), toast.duration);
    }
    return id;
  }, [dismiss]);

  const api = {
    success: (message, opts) => push({ variant: 'success', message, ...opts }),
    error:   (message, opts) => push({ variant: 'error',   message, ...opts }),
    warning: (message, opts) => push({ variant: 'warning', message, ...opts }),
    info:    (message, opts) => push({ variant: 'info',    message, ...opts }),
    custom:  (opts) => push(opts),
    dismiss,
  };

  return (
    <ToastContext.Provider value={{ toast: api }}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, dismiss }) => (
  <div
    style={{
      position: 'fixed',
      top: 16,
      right: 16,
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      maxWidth: 400,
      pointerEvents: 'none',
    }}
  >
    {toasts.map((t) => (
      <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
    ))}
  </div>
);

const ToastItem = ({ toast, onDismiss }) => {
  const v = VARIANT_STYLES[toast.variant] || VARIANT_STYLES.info;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        background: v.background,
        color: v.color,
        border: `1px solid ${v.borderColor}`,
        borderRadius: 10,
        padding: '12px 14px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        minWidth: 280,
        transition: 'transform 200ms ease-out, opacity 200ms ease-out',
        transform: visible ? 'translateX(0)' : 'translateX(12px)',
        opacity: visible ? 1 : 0,
      }}
    >
      <Icon icon={v.icon} width="22" />
      <div style={{ flex: 1 }}>
        {toast.title && (
          <span style={{ display: 'block', fontWeight: 700, fontSize: 13, marginBottom: 2 }}>
            {toast.title}
          </span>
        )}
        <span style={{ display: 'block', fontSize: 13 }}>{toast.message}</span>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fechar"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          padding: 4,
          marginLeft: 4,
          opacity: 0.7,
        }}
      >
        <Icon icon="mdi:close" width="14" />
      </button>
    </div>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve ser usado dentro de <ToastProvider>');
  }
  return ctx;
};

export default ToastContext;
